import { Box, DotsLoader } from '@drone-js/core';
import WalletCard from '@v2/components/Wallet/WalletCard';
import { useServiceResolver } from '@services/useServiceResolver';
import { useSetNavHeader } from '@v2/contexts/navigation/hooks/useSetNavHeader';
import React from 'react';
import { usePageBackground } from '../hooks/usePageBackground';
import { useWalletContext } from '../../contexts/wallet/hooks/useWalletContext';
import { useStore } from 'zustand';
import { PinForm, PinFormProps } from '@v2/components/Forms/PinForm/PinForm';
import { WalletOtpForm, WalletOtpFormProps } from '@v2/components/Forms/WalletOtpForm/WalletOtpForm';
import { usePushNotification, useRenderNotification } from '@components/Notification/useNotification';
import { WithWalletScaContext } from './hoc/withWalletScaContext';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from '@contexts/translation/hooks/useTranslation';
import { ScaPanelCmsWithDefault } from './ScaPanelCmsWithDefault';
import { walletLogoUrl } from '@v2/utils/constants';
import { useWebAnalyticBinder } from '@components/panels/SignupPanel/useWebAnalyticBinder';
import { ScaPageBinder } from './analytics';
import { usePasskeyContext } from '@contexts/passkey/hooks/usePasskeyContext';
import { useAppSetting } from '@contexts/appSetting/hooks/useAppSetting';
import { ResponseCode } from '@ul/api';

const ScaPanel: React.FC = () => {
    useWebAnalyticBinder(ScaPageBinder);
    const { sdkEffect, logger } = useServiceResolver();
    const { setBackgroundColor } = usePageBackground();

    const { isSupported, abortPasskeyRequestOnPlatform, performPasskeyAuthentication } = usePasskeyContext();
    const { feature } = useAppSetting();

    const isPasskeyEnabled = isSupported && Boolean(feature?.passkeyAuthentication);
    const isWalletPasskeyEnabled = Boolean(feature?.walletEnablePasskey) && isPasskeyEnabled;

    const [showPasskeyButton, setShowPasskeyButton] = React.useState(false);
    const cancelledRef = React.useRef(false);

    const { walletStorage, onPasskeyAuth, onInit } = useWalletContext();

    const wrappedOnPasskeyAuth = (responseCode: ResponseCode) => {
        if (cancelledRef.current || !isWalletPasskeyEnabled) return;
        onPasskeyAuth?.(responseCode);
    };
    const { walletAuthState, setTransactionData, setWalletAuthState } = useStore(
        walletStorage,
        useShallow(state => ({
            walletAuthState: state.walletAuthState,
            setTransactionData: state.setTransactionData,
            setWalletAuthState: state.setWalletAuthState,
        }))
    );

    const renderNotification = useRenderNotification('WalletSca');
    const pushNotification = usePushNotification('WalletSca');

    useSetNavHeader('Agoda Wallet', sdkEffect.onExit, false, walletLogoUrl);
    setBackgroundColor('base.background');

    const translation = useTranslation(ScaPanelCmsWithDefault);

    const handlePasskeyAuth = async () => {
        if (!isPasskeyEnabled || !wrappedOnPasskeyAuth) return;
        try {
            abortPasskeyRequestOnPlatform();
            const { transactionInfo } = walletStorage.getState();
            await performPasskeyAuthentication(wrappedOnPasskeyAuth, false, false, undefined, {
                mfaOperationCode: transactionInfo?.mfaOperationCode,
                transactionId: transactionInfo?.transactionId,
            });
        } catch (error) {
            logger.error('Wallet SCA passkey authentication error', error);
            if (cancelledRef.current) return;
            pushNotification({
                color: 'error',
                message: translation.TechnicalDifficulty,
                testId: 'passkey-auth-error',
            });
        }
    };

    // To be updated with CMS
    const pinFormProps: PinFormProps = {
        cms: {
            initial: {
                title: translation.ConfirmPinTitle,
                caption: translation.ConfirmPinCaption,
            },
        },
        currentPath: 'walletSca',
        showForgotPin: true,
        showPasskeyButton: showPasskeyButton,
        onPasskeyClick: handlePasskeyAuth,
    };

    const otpFormProps: WalletOtpFormProps = {
        showPasskeyButton: showPasskeyButton,
        onPasskeyClick: handlePasskeyAuth,
    };

    const renderForm = () => {
        switch (walletAuthState) {
            case 'loading':
                return renderLoadingForm();
            case 'otp':
                return <WalletOtpForm {...otpFormProps} />;
            case 'pin':
                return <PinForm {...pinFormProps} />;
        }
    };

    const renderLoadingForm = () => (
        <Box data-cy="wallet-sca-loading-container" width="100%" p="16" display="flex" justifyContent="center">
            <DotsLoader variant="brand" />
        </Box>
    );

    const getTransactionData = () => sdkEffect.onTransactionWillLoad();

    React.useEffect(() => {
        cancelledRef.current = false;

        const initializeAuth = async () => {
            try {
                const response = await getTransactionData();
                if (cancelledRef.current) return;
                setTransactionData(response);
                setWalletAuthState('pin');
            } catch (error) {
                logger.error('RPC onTransactionWillLoad error', error);
                if (cancelledRef.current) return;
                pushNotification({
                    color: 'error',
                    message: translation.TechnicalDifficulty,
                    testId: 'technical-difficulty',
                });
                return;
            }
            if (cancelledRef.current || !isWalletPasskeyEnabled) return;
            try {
                const initResponse = await onInit?.();
                if (cancelledRef.current) return;
                const hasPasskey = !!initResponse?.passkey?.has;
                if (hasPasskey) {
                    await handlePasskeyAuth();
                }
                if (!cancelledRef.current) {
                    setShowPasskeyButton(hasPasskey);
                }
            } catch (error) {
                if (!cancelledRef.current) setShowPasskeyButton(false);
                logger.error('SCA init error', error);
            }
        };

        initializeAuth();

        return () => {
            cancelledRef.current = true;
        };
    }, []);

    return (
        <>
            {renderNotification()}
            <WalletCard mx="auto" data-cy="wallet-auth-panel" p="12">
                {renderForm()}
            </WalletCard>
        </>
    );
};

const WalletScaPanel = WithWalletScaContext(ScaPanel);

export { WalletScaPanel };
